import { useState } from 'react';
import { Modal } from './parts';
import { CardIcon, RoleIcon } from './Icon';
import { CardInfo } from './CardInfo';
import type { Card, PublicPlayer } from '../game/types';
import { cardAccent, characterAbilityKey, characterName, roleColor } from '../game/cardMeta';
import { cardArt, characterArt, roleArt } from '../game/art';
import { useT } from '../i18n';

/**
 * Details on a runner at the table: status, revealed role (if any), and the
 * public character + its ability. Roles stay hidden until revealed (death, or
 * the Director from the start); characters are public info.
 */
export function PlayerInfo({
  player,
  isMe,
  onClose,
}: {
  player: PublicPlayer;
  isMe: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const [gearCard, setGearCard] = useState<Card | null>(null);
  const role = player.roleRevealed ? player.revealedRole ?? null : null;
  const character = player.characterId ? characterName[player.characterId] : null;
  const characterImage = characterArt(player.characterId);
  const abilityKey = player.characterId ? characterAbilityKey[player.characterId] : null;
  const ability = abilityKey ? t.ability[abilityKey] : null;

  return (
    <Modal onClose={onClose}>
      <div className="player-sheet">
        <div className="player-sheet-head">
          <div>
            <h2 className="h2">{player.displayName}{isMe ? ` (${t.player.you})` : ''}</h2>
            <div className="player-sheet-meta">
              <span className="tag" style={{ color: player.alive ? 'var(--lime)' : 'var(--magenta)' }}>
                {player.alive ? t.player.online : t.player.flatlined}
              </span>
              {player.alive && (
                <span>{t.player.integrity} {player.integrity}/{player.maxIntegrity}</span>
              )}
              <span>✋ {player.handCount}</span>
            </div>
          </div>
          <button className="link" onClick={onClose}>{t.player.close}</button>
        </div>

        {characterImage && (
          <div
            className="player-info-hero"
            style={{
              backgroundImage: `linear-gradient(90deg, rgba(4,6,12,0.1), rgba(4,6,12,0.82)), url(${characterImage})`,
            }}
          >
            <div className="player-hero-caption">
              <span className="eyebrow">{t.player.character}</span>
              <strong>{character}</strong>
            </div>
          </div>
        )}

        <div className="player-info-grid">
          <div
            className="panel col player-info-role"
            style={{
              ...(role ? { backgroundImage: `linear-gradient(90deg, rgba(4,6,12,0.78), rgba(4,6,12,0.92)), url(${roleArt(role)})` } : {}),
            }}
          >
            <span className="eyebrow">{t.player.role}</span>
            {role ? (
              <>
                <span className="row player-role-name" style={{ color: roleColor[role] }}>
                  <RoleIcon role={role} size={16} /> {t.role.name[role]}
                </span>
                <span className="mono-dim player-panel-copy">
                  {t.role.goal[role]}
                </span>
              </>
            ) : (
              <span className="mono-dim player-panel-copy">{t.player.roleUnknown}</span>
            )}
          </div>

          {character && (
            <div className="panel col player-info-character">
              <div className="row between">
                <span className="eyebrow">{t.player.character}</span>
                <span className="player-character-name">{character}</span>
              </div>
              {ability && (
                <span className="mono-dim player-panel-copy">{ability}</span>
              )}
            </div>
          )}
        </div>

        {player.equippedGear.length > 0 && (
          <div className="player-gear-section">
            <span className="eyebrow">{t.player.gear}</span>
            <div className="player-gear-cards">
              {player.equippedGear.map((g) => (
                <button
                  key={g.id}
                  className={'equipped-card ' + (cardAccent[g.kind] ?? 'a-violet')}
                  onClick={() => setGearCard(g)}
                >
                  <span className="equipped-card-art" style={{ backgroundImage: `url(${cardArt(g.kind)})` }} />
                  <span className="equipped-card-mark"><CardIcon kind={g.kind} size={14} /></span>
                  <span className="equipped-card-copy">
                    <strong>{t.card.name[g.kind]}</strong>
                    <span>{t.card.desc[g.kind]}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {player.equippedGear.length === 0 && (
          <div className="player-gear-section empty">
            <span className="eyebrow">{t.player.gear}</span>
            <span className="mono-dim player-panel-copy">—</span>
          </div>
        )}
      </div>
      {gearCard && <CardInfo card={gearCard} onClose={() => setGearCard(null)} />}
    </Modal>
  );
}
